import { Button } from '../ui/button';
import { Flame } from 'lucide-react';

const ProductCard = ({ product, onAdd, hasAddons, isExpanded, onExpand, isFavourite }) => (
  <button
    data-testid={`product-${product.id}`}
    onClick={() => {
      if (product.stock_qty <= 0) return;
      onAdd(product);
      if (hasAddons) onExpand(isExpanded ? null : product.id);
    }}
    className={`product-card touch-press ${product.stock_qty <= 0 ? 'out-of-stock' : ''} ${isExpanded ? 'ring-2 ring-amber-400' : ''} ${product.is_addon ? 'border-l-4 border-l-amber-400' : ''} ${isFavourite ? 'border-t-2 border-t-orange-400' : ''}`}
  >
    {isFavourite && (
      <span className="absolute top-1 right-1" data-testid={`favourite-badge-${product.id}`}>
        <Flame className="w-3.5 h-3.5 text-orange-400" />
      </span>
    )}
    <span className="font-body font-medium text-sm text-center text-slate-800 leading-tight">
      {product.name}
    </span>
    <span className="price-display text-xl text-slate-900 mt-2">
      £{product.price_inc_vat.toFixed(2)}
    </span>
    {hasAddons && (
      <span className="text-[10px] text-amber-600 font-medium mt-0.5">+ add-ons</span>
    )}
    {product.stock_qty <= product.low_stock_threshold && product.stock_qty > 0 && (
      <span className="low-stock-badge mt-1">{product.stock_qty} left</span>
    )}
    {product.stock_qty <= 0 && (
      <span className="text-xs text-red-500 font-medium mt-1">Out of stock</span>
    )}
  </button>
);

export const ProductGrid = ({ products, categories, activeCategory, displayGroup, onAddToBasket, expandedProduct, onExpand, allProducts, favourites = {} }) => {
  let filteredProducts = products.filter(p => !p.archived);
  
  if (displayGroup) {
    const groupCatIds = categories
      .filter(c => c.display_group === displayGroup)
      .map(c => c.id);
    filteredProducts = filteredProducts.filter(p => groupCatIds.includes(p.category_id));
  } else if (activeCategory) {
    // When Add-ons category is selected, show all add-on products regardless of their home category
    const activeCat = categories.find(c => c.id === activeCategory);
    if (activeCat && activeCat.name === 'Add-ons') {
      filteredProducts = filteredProducts.filter(p => p.is_addon);
    } else {
      filteredProducts = filteredProducts.filter(p => p.category_id === activeCategory);
    }
  }

  // Sort favourites first for the active category
  const catFavs = activeCategory ? (favourites[activeCategory] || []) : [];
  if (catFavs.length > 0) {
    const favSet = new Set(catFavs);
    filteredProducts = [
      ...filteredProducts.filter(p => favSet.has(p.id)),
      ...filteredProducts.filter(p => !favSet.has(p.id))
    ];
  }

  // Build a set of all favourite product IDs across all categories for quick lookup
  const allFavIds = new Set(Object.values(favourites).flat());

  const getAddonsFor = (product) => {
    return allProducts.filter(p =>
      p.is_addon && !p.archived && (
        p.addon_for_products.includes(product.id) ||
        p.addon_for_categories.includes(product.category_id)
      )
    );
  };

  const groupedProducts = displayGroup ? filteredProducts.reduce((acc, product) => {
    const cat = categories.find(c => c.id === product.category_id);
    const catName = cat?.name || 'Other';
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(product);
    return acc;
  }, {}) : null;

  const renderProduct = (product) => {
    const addons = getAddonsFor(product);
    const isExpanded = expandedProduct === product.id;
    const isFav = allFavIds.has(product.id);

    return (
      <div key={product.id} className="relative">
        <ProductCard product={product} onAdd={onAddToBasket} hasAddons={addons.length > 0}
          isExpanded={isExpanded} onExpand={onExpand} isFavourite={isFav} />
        {isExpanded && addons.length > 0 && (
          <div className="absolute top-full left-0 z-20 mt-1 p-2 bg-white rounded-lg shadow-lg border border-amber-200 min-w-[220px]"
            data-testid={`addons-for-${product.id}`}>
            <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5 px-1">Add-ons</p>
            <div className="grid grid-cols-2 gap-1.5">
              {addons.map(addon => (
                <button key={addon.id} data-testid={`addon-${addon.id}`}
                  onClick={(e) => { e.stopPropagation(); addon.stock_qty > 0 && onAddToBasket(addon, true); }}
                  className={`rounded-md px-2 py-1.5 text-left border border-amber-200 bg-amber-50 hover:bg-amber-100 active:scale-95 transition-all ${addon.stock_qty <= 0 ? 'opacity-40' : ''}`}>
                  <span className="text-xs font-medium text-amber-900 block leading-tight truncate">{addon.name}</span>
                  <span className="text-xs font-bold text-amber-700">+£{addon.price_inc_vat.toFixed(2)}</span>
                </button>
              ))}
            </div>
            <button onClick={(e) => { e.stopPropagation(); onExpand(null); }}
              className="w-full mt-1.5 rounded-md py-1 text-xs text-slate-500 font-medium hover:bg-slate-50 border border-slate-200">
              Done
            </button>
          </div>
        )}
      </div>
    );
  };

  if (displayGroup && groupedProducts) {
    return (
      <div className="flex-1 overflow-y-auto bg-slate-50 p-4 custom-scrollbar">
        {Object.entries(groupedProducts).map(([catName, prods]) => (
          <div key={catName} className="mb-6">
            <h3 className="font-heading text-lg font-bold text-slate-700 mb-3 px-2">{catName}</h3>
            <div className="product-grid">
              {prods.map(renderProduct)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="product-grid custom-scrollbar bg-slate-50">
      {filteredProducts.length === 0 ? (
        <div className="col-span-full flex items-center justify-center text-slate-400 py-12">
          No products in this category
        </div>
      ) : (
        filteredProducts.map(renderProduct)
      )}
    </div>
  );
};
